import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Paginate from '../utils/Paginate';
import { convert } from '../utils/convertDate';
import { BsThreeDots } from 'react-icons/bs';

// Interface definitions remain the same
// interface Column {
//   key: string;
//   header: string;
//   render?: (item: any) => React.ReactNode;
// }

// interface GeneralTableProps {
//   columns: Column[];
//   data: any[];
//   nextPage: boolean;
//   pageIndex: number;
//   totalCount: number;
//   totalPages: number;
//   prevPage: () => void;
//   setPageIndex: (page: number) => void;
//   actions?: string[]; // Array of action labels, or (item) => string[]
//   onActionSelect?: (action: string, item: any) => void; // Callback for action selection
//   emptyMessage?: string; // Shown when data is empty
// }

const GeneralTable = ({
  columns,
  data,
  nextPage,
  pageIndex,
  totalCount,
  totalPages,
  prevPage,
  setPageIndex,
  actions = [], // Can be array or function(item) => array
  onActionSelect, // Optional callback
  emptyMessage = 'No records found',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(null); // Track which row's dropdown is open
  const [dropdownPosition, setDropdownPosition] = useState({}); // Store dropdown position
  const buttonRefs = useRef({});

  const handleActionSelect = (action, item) => {
    if (onActionSelect) {
      onActionSelect(action, item);
      setIsDropdownOpen(null); // Close dropdown after selection
    }
  };

  const toggleDropdown = (index, event, item) => {
    if (isDropdownOpen === index) {
      setIsDropdownOpen(null);
    } else {
      setIsDropdownOpen(index);
      // Store button reference and calculate position
      if (event?.currentTarget) {
        buttonRefs.current[index] = event.currentTarget;
        const rect = event.currentTarget.getBoundingClientRect();
        const dropdownWidth = 192; // w-48 = 192px
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // Get actions for this item to calculate height
        const itemActions = typeof actions === 'function' ? actions(item) : actions;
        const estimatedHeight = itemActions.length * 40 + 16; // Approximate dropdown height
        
        // Determine if dropdown should open upward or downward
        // Using fixed positioning, so coordinates are relative to viewport (no scroll offset needed)
        let top, left;
        if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
          // Open upward
          top = rect.top - estimatedHeight - 8;
        } else {
          // Open downward
          top = rect.bottom + 8;
        }
        
        // Calculate horizontal position (right-align by default)
        left = rect.right - dropdownWidth;
        
        // Ensure dropdown doesn't go off-screen on the left
        if (left < 0) {
          left = rect.left;
        }
        
        // Ensure dropdown doesn't go off-screen on the right
        if (left + dropdownWidth > window.innerWidth) {
          left = window.innerWidth - dropdownWidth - 8;
        }
        
        setDropdownPosition({
          ...dropdownPosition,
          [index]: { top, left }
        });
      }
    }
  };

  // Close dropdown when clicking outside and update position on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen !== null) {
        const button = buttonRefs.current[isDropdownOpen];
        const dropdown = document.getElementById(`dropdown-${isDropdownOpen}`);
        if (
          button &&
          !button.contains(event.target) &&
          dropdown &&
          !dropdown.contains(event.target)
        ) {
          setIsDropdownOpen(null);
        }
      }
    };

    const updatePosition = () => {
      if (isDropdownOpen !== null) {
        const button = buttonRefs.current[isDropdownOpen];
        if (button) {
          const rect = button.getBoundingClientRect();
          const dropdownWidth = 192;
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          // Get actions for the current item to calculate height
          const currentItem = data.find((_, idx) => idx === isDropdownOpen);
          const itemActions = currentItem && typeof actions === 'function' ? actions(currentItem) : (typeof actions === 'function' ? [] : actions);
          const estimatedHeight = itemActions.length * 40 + 16;
          
          // Using fixed positioning, so coordinates are relative to viewport (no scroll offset needed)
          let top, left;
          if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
            top = rect.top - estimatedHeight - 8;
          } else {
            top = rect.bottom + 8;
          }
          
          left = rect.right - dropdownWidth;
          if (left < 0) {
            left = rect.left;
          }
          
          // Ensure dropdown doesn't go off-screen on the right
          if (left + dropdownWidth > window.innerWidth) {
            left = window.innerWidth - dropdownWidth - 8;
          }
          
          setDropdownPosition(prev => ({
            ...prev,
            [isDropdownOpen]: { top, left }
          }));
        }
      }
    };

    if (isDropdownOpen !== null) {
      // Initial position update
      updatePosition();
      
      document.addEventListener('mousedown', handleClickOutside);
      // Listen to scroll events on window and all scrollable containers
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      // Also listen to scroll on the table container if it exists
      const tableContainer = document.querySelector('.overflow-x-auto');
      if (tableContainer) {
        tableContainer.addEventListener('scroll', updatePosition);
      }
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
        if (tableContainer) {
          tableContainer.removeEventListener('scroll', updatePosition);
        }
      };
    }
  }, [isDropdownOpen, actions, data]);

  return (
    <div>
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div className="relative ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full table-fixed divide-y divide-gray-300">
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="min-w-[9rem] py-3.5 pl-5 pr-3 italic font-normal text-left text-sm text-gray-400"
                      >
                        {column.header}
                      </th>
                    ))}
                    <th scope="col" className="px-3 py-3.5 text-left text-sm italic font-normal text-gray-400">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white relative">
                  {data.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-3 py-10 text-center text-sm text-gray-500"
                      >
                        {emptyMessage}
                      </td>
                    </tr>
                  )}
                  {data.map((item, i) => (
                    <tr key={i} className={item.isDeleted ? "bg-red-100" : "bg-gray-50"}>
                      {columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className="whitespace-nowrap px-3 py-4 text-sm text-gray-900"
                        >
                          {column.render ? column.render(item) : item[column.key]}
                        </td>
                      ))}
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        <div className="relative">
                          {(() => {
                            // Get actions for this specific item (support both array and function)
                            const itemActions = typeof actions === 'function' ? actions(item) : actions;
                            return (
                              <>
                                <button
                                  onClick={(e) => toggleDropdown(i, e, item)}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  <BsThreeDots />
                                </button>
                                {isDropdownOpen === i && itemActions.length > 0 && dropdownPosition[i] && createPortal(
                                  <div
                                    id={`dropdown-${i}`}
                                    className="fixed w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[9999]"
                                    style={{
                                      top: `${dropdownPosition[i].top}px`,
                                      left: `${dropdownPosition[i].left}px`,
                                    }}
                                  >
                                    <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                                      {itemActions.map((action, actionIndex) => (
                                        <button
                                          key={actionIndex}
                                          onClick={() => handleActionSelect(action, item)}
                                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                          role="menuitem"
                                        >
                                          {action}
                                        </button>
                                      ))}
                                    </div>
                                  </div>,
                                  document.body
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <Paginate
        nextPage={nextPage}
        pageIndex={pageIndex}
        totalCount={totalCount}
        totalPages={totalPages}
        prevPage={prevPage}
        setPageIndex={setPageIndex}
      />
    </div>
  );
};

export default GeneralTable;